import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, Subtitle, PrimaryButton, SecondaryButton,
  PageHeader, HeaderActions, TitleRow, IconButton, ToggleSwitch,
} from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import { API_BASE } from '../config';
import { buildBundlePresets } from '../utils/bundlePresets';
import { useConfirm } from '../components/ui/Modal';
import { typeLabel } from '../utils/dictionaryLabel';
import SimpleSelect from '../ui/SimpleSelect';
import { parametrSkladnikow, rodzajZnacznika, polaczOstrzezenia, trescPolaczona } from '../utils/bundleWarnings';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

const emptyForm = {
  name: '',
  active: true,
  daily_price: '',
  // 071: cena za dzień przy wynajmie dłuższym niż 7 dni, pusta = zwykła cena zestawu.
  daily_price_over_7: '',
  type_ids: [],
  // 070: wymagana klasa składnika ({ typeId: classId }, brak wpisu = dowolna) i czego dotyczy zestaw.
  class_by_type: {},
  applies_to: 'all',
};

function csrfHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': localStorage.getItem('csrfToken') || '',
  };
}

function bundleToForm(bundle) {
  return {
    name: bundle.name || '',
    active: bundle.active !== false,
    daily_price: bundle.dailyPrice != null ? String(bundle.dailyPrice) : '',
    daily_price_over_7: bundle.dailyPriceOver7 != null ? String(bundle.dailyPriceOver7) : '',
    type_ids: Array.isArray(bundle.typeIds) ? bundle.typeIds : [],
    class_by_type: Object.fromEntries((bundle.items || []).filter((i) => i.classId != null).map((i) => [i.typeId, String(i.classId)])),
    applies_to: bundle.appliesTo || 'all',
  };
}

// Składniki do zapisu: kategoria z wymaganą klasą albo bez (070).
function itemsPayload(typeIds, classByType) {
  return typeIds.map((typeId) => ({
    type_id: typeId,
    class_id: classByType && classByType[typeId] ? Number(classByType[typeId]) : null,
  }));
}

export default function EquipmentBundles() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();

  const [bundles, setBundles] = useState([]);
  const [types, setTypes] = useState([]);
  const [classes, setClasses] = useState([]);
  // Ostrzeżenia dla zestawu w formularzu (GET /api/bundle-check), liczone przy każdej zmianie.
  const [formWarnings, setFormWarnings] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  // P7.6: id zestawu, którego toggle active właśnie zapisujemy (disabled w trakcie).
  const [togglingId, setTogglingId] = useState(null);
  const formRef = useRef(null);

  // Formularz renderuje się PO karcie "Szybkie zestawy" (presety) — przy kilku
  // pasujących typach sprzętu potrafi wylądować poniżej ekranu, więc "Nowy zestaw"/
  // "Edytuj" (oba przełączają ten sam formOpen) same przewijają do niego widok.
  useEffect(() => {
    if (formOpen && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [formOpen]);

  const loadBundles = () => {
    setLoading(true);
    axios.get(`${API_BASE}/api/equipment-bundles`, { withCredentials: true })
      .then((res) => { setBundles(Array.isArray(res.data) ? res.data : []); setError(''); })
      .catch(() => setError(t('equipmentBundles.loadError')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBundles();
    axios.get(`${API_BASE}/api/types`, { withCredentials: true })
      .then((res) => setTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTypes([]));
    axios.get(`${API_BASE}/api/classes`, { withCredentials: true })
      .then((res) => setClasses(Array.isArray(res.data) ? res.data : []))
      .catch(() => setClasses([]));
    // Podpowiedzi są dodatkiem: błąd zapytania ukrywa kartę, strona działa dalej.
    axios.get(`${API_BASE}/api/bundle-suggestions`, { withCredentials: true })
      .then((res) => setSuggestions(Array.isArray(res.data?.suggestions) ? res.data.suggestions : []))
      .catch(() => setSuggestions([]));
  }, []);

  const typeById = useMemo(() => {
    const map = {};
    types.forEach((tp) => { map[tp.id] = tp.name; });
    return map;
  }, [types]);

  const classById = useMemo(() => {
    const map = {};
    classes.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [classes]);

  // Ostrzeżenia na żywo: chwilę po ostatniej zmianie składu, cen albo zakresu. Spóźniona odpowiedź
  // (szybkie pisanie) nie nadpisuje nowszej.
  const checkSeq = useRef(0);
  useEffect(() => {
    if (!formOpen || form.type_ids.length < 2 || form.daily_price === '') { setFormWarnings([]); return undefined; }
    const nr = ++checkSeq.current;
    const zegar = setTimeout(() => {
      const params = new URLSearchParams({
        items: parametrSkladnikow(form.type_ids, form.class_by_type),
        daily: String(form.daily_price),
        over7: String(form.daily_price_over_7 || ''),
        applies: form.applies_to || 'all',
      });
      axios.get(`${API_BASE}/api/bundle-check?${params}`, { withCredentials: true })
        .then((res) => { if (nr === checkSeq.current) setFormWarnings(Array.isArray(res.data?.warnings) ? res.data.warnings : []); })
        .catch(() => { if (nr === checkSeq.current) setFormWarnings([]); });
    }, 300);
    return () => clearTimeout(zegar);
  }, [formOpen, form.type_ids, form.class_by_type, form.daily_price, form.daily_price_over_7, form.applies_to]);

  // Szybkie zestawy: typy wypożyczane razem w historii rezerwacji firmy (dowolna branża).
  // Zestaw, który już istnieje, nie jest podpowiadany ponownie.
  const bundlePresets = useMemo(
    () => buildBundlePresets({ suggestions, types, bundles }),
    [suggestions, types, bundles]
  );

  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setFormOpen(true);
  };

  const openEditForm = (bundle) => {
    setEditingId(bundle.id);
    setForm(bundleToForm(bundle));
    setFormError('');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
  };

  const toggleType = (typeId) => {
    setForm((prev) => ({
      ...prev,
      type_ids: prev.type_ids.includes(typeId)
        ? prev.type_ids.filter((id) => id !== typeId)
        : [...prev.type_ids, typeId].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const name = form.name.trim();
    if (!name) { setFormError(t('equipmentBundles.nameRequired')); return; }

    const dailyPrice = Number(form.daily_price);
    if (!Number.isFinite(dailyPrice) || form.daily_price === '' || dailyPrice < 0) {
      setFormError(t('equipmentBundles.priceRequired'));
      return;
    }

    if (form.type_ids.length < 2) {
      setFormError(t('equipmentBundles.typesRequired'));
      return;
    }

    const over7 = form.daily_price_over_7 === '' ? null : Number(form.daily_price_over_7);
    if (over7 !== null && (!Number.isFinite(over7) || over7 < 0)) {
      setFormError(t('equipmentBundles.over7Invalid'));
      return;
    }

    const payload = {
      name,
      active: form.active,
      daily_price: dailyPrice,
      daily_price_over_7: over7,
      items: itemsPayload(form.type_ids, form.class_by_type),
      applies_to: form.applies_to || 'all',
    };

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_BASE}/api/equipment-bundles/${editingId}`, payload, {
          withCredentials: true, headers: csrfHeaders(),
        });
      } else {
        await axios.post(`${API_BASE}/api/equipment-bundles`, payload, {
          withCredentials: true, headers: csrfHeaders(),
        });
      }
      closeForm();
      loadBundles();
    } catch (err) {
      setFormError(err?.response?.data?.message || t('equipmentBundles.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Klik w preset TYLKO prefilluje formularz (nazwa, typy, sugerowana cena) —
  // zapis idzie normalną ścieżką handleSubmit, nic nie tworzymy od razu.
  const applyBundlePreset = (preset) => {
    setEditingId(null);
    setForm({
      name: preset.name,
      active: true,
      daily_price: preset.suggestedPrice != null ? String(preset.suggestedPrice) : '',
      daily_price_over_7: '',
      type_ids: [...preset.typeIds].sort((a, b) => a - b),
      class_by_type: {},
      applies_to: 'all',
    });
    setFormError('');
    setFormOpen(true);
  };

  // PUT wymaga pełnego payloadu (name, daily_price, type_ids ≥2, active) —
  // flipujemy tylko active na danych zestawu z listy.
  const toggleBundleActive = async (bundle) => {
    const typeIds = Array.isArray(bundle.typeIds) ? bundle.typeIds : [];
    if (typeIds.length < 2) {
      setError(t('equipmentBundles.toggleNeedsTypes'));
      return;
    }
    setError('');
    setTogglingId(bundle.id);
    try {
      await axios.put(`${API_BASE}/api/equipment-bundles/${bundle.id}`, {
        name: bundle.name,
        active: bundle.active === false,
        daily_price: Number(bundle.dailyPrice),
        daily_price_over_7: bundle.dailyPriceOver7 != null ? Number(bundle.dailyPriceOver7) : null,
        items: (bundle.items && bundle.items.length ? bundle.items : typeIds.map((typeId) => ({ typeId, classId: null })))
          .map((i) => ({ type_id: i.typeId, class_id: i.classId != null ? i.classId : null })),
        applies_to: bundle.appliesTo || 'all',
      }, { withCredentials: true, headers: csrfHeaders() });
      loadBundles();
    } catch (err) {
      setError(err?.response?.data?.message || t('equipmentBundles.toggleError'));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (bundle) => {
    const ok = await confirm({ title: t('equipmentBundles.deleteConfirm'), tone: 'danger' });
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE}/api/equipment-bundles/${bundle.id}`, {
        withCredentials: true, headers: csrfHeaders(),
      });
      loadBundles();
    } catch (err) {
      setError(err?.response?.data?.message || t('equipmentBundles.deleteError'));
    }
  };

  const formatTypes = (bundle) => {
    if (!Array.isArray(bundle.typeIds) || !bundle.typeIds.length) return '-';
    const klasa = new Map((bundle.items || []).map((i) => [i.typeId, i.classId]));
    const sklad = bundle.typeIds.map((id) => {
      const nazwa = typeById[id] || `#${id}`;
      const c = klasa.get(id);
      return c != null ? `${nazwa} · ${classById[c] || `#${c}`}` : nazwa;
    }).join(' + ');
    if (bundle.appliesTo === 'classes') return `${sklad} (${t('priceRules.scopeClassesTag')})`;
    if (bundle.appliesTo === 'equipment') return `${sklad} (${t('priceRules.scopeEquipmentTag')})`;
    return sklad;
  };

  return (
    <PanelContainer>
      <PageHeader>
        <TitleRow>
          <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
            <ArrowLeftIcon />
          </IconButton>
          <div>
            <Title>{t('equipmentBundles.title')}</Title>
            <Subtitle>{t('equipmentBundles.subtitle')}</Subtitle>
          </div>
        </TitleRow>
        <HeaderActions data-tour="bundles-new">
          <PrimaryButton data-cy="equipment-bundles-new" onClick={openNewForm}>{t('equipmentBundles.newBundle')}</PrimaryButton>
        </HeaderActions>
      </PageHeader>
      <TylkoOdczytWPrzerwie>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {bundlePresets.length > 0 && (
        <PresetsCard data-cy="bundle-presets">
          <FormTitle>{t('equipmentBundles.presets.title')}</FormTitle>
          <FormHint>{t('equipmentBundles.presets.hint')}</FormHint>
          <PresetGrid>
            {bundlePresets.map((preset) => (
              <PresetCard
                key={preset.key}
                type="button"
                data-cy={`bundle-preset-${preset.key}`}
                aria-label={t('equipmentBundles.presets.useAria').replace('{name}', preset.name)}
                onClick={() => applyBundlePreset(preset)}
              >
                <strong>{preset.typeNames.map((name) => typeLabel(t, name)).join(' + ')}</strong>
                <span>{t('equipmentBundles.presets.timesRented', { count: preset.times })}</span>
                {preset.suggestedPrice != null ? (
                  <em>
                    {t('equipmentBundles.presets.priceLine')
                      .replace('{price}', preset.suggestedPrice)
                      .replace('{sum}', preset.sumIndividual)
                      .replace('{pct}', preset.discountPct)}
                  </em>
                ) : (
                  <em>{t('equipmentBundles.presets.noPrice')}</em>
                )}
              </PresetCard>
            ))}
          </PresetGrid>
        </PresetsCard>
      )}

      {formOpen && (
        <FormCard ref={formRef} onSubmit={handleSubmit}>
          <FormTitle>{editingId ? t('equipmentBundles.editBundle') : t('equipmentBundles.newBundle')}</FormTitle>

          {formError && <ErrorBanner data-cy="equipment-bundles-error">{formError}</ErrorBanner>}

          <FormRow>
            <FormField style={{ flex: 2 }}>
              <FormLabel>{t('equipmentBundles.name')}</FormLabel>
              <FormInput
                data-cy="equipment-bundles-name"
                value={form.name}
                placeholder={t('equipmentBundles.namePlaceholder')}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>{t('equipmentBundles.dailyPrice')}</FormLabel>
              <FormInput
                data-cy="equipment-bundles-price"
                type="number"
                step="0.01"
                min="0"
                value={form.daily_price}
                onChange={(e) => setForm((p) => ({ ...p, daily_price: e.target.value }))}
              />
              <FormHint>{t('equipmentBundles.dailyPriceHint')}</FormHint>
            </FormField>
            <FormField>
              <FormLabel>{t('equipmentBundles.dailyPriceOver7')}</FormLabel>
              <FormInput
                data-cy="equipment-bundles-price-over7"
                type="number"
                step="0.01"
                min="0"
                value={form.daily_price_over_7}
                placeholder={t('equipmentBundles.dailyPriceOver7Placeholder')}
                onChange={(e) => setForm((p) => ({ ...p, daily_price_over_7: e.target.value }))}
              />
              <FormHint>{t('equipmentBundles.dailyPriceOver7Hint')}</FormHint>
            </FormField>
            {/* Bez pola „Aktywny” (26.10.2026): nowy zestaw startuje aktywny, edycja zostawia stan
                bez zmian, a włącza i wyłącza zestaw przełącznik w kolumnie „Status” na liście. */}
          </FormRow>

          <FormField>
            <FormLabel>{t('equipmentBundles.types')}</FormLabel>
            <TypeRow>
              {types.map((tp) => (
                <TypeChip
                  key={tp.id}
                  type="button"
                  data-cy="equipment-bundles-type"
                  $active={form.type_ids.includes(tp.id)}
                  onClick={() => toggleType(tp.id)}
                >
                  {tp.name}
                </TypeChip>
              ))}
            </TypeRow>
            {/* Zestaw wymaga min. 2 typów, więc firma bez ani jednego widziałaby pusty rząd
                i zablokowany zapis bez wyjaśnienia dlaczego. */}
            <FormHint data-cy="equipment-bundles-types-hint">
              {types.length === 0 ? t('equipmentBundles.noTypes') : t('equipmentBundles.typesHint')}
            </FormHint>
          </FormField>

          {/* 070: składnik może wymagać klasy (Narty · Premium + Buty · Premium). */}
          {form.type_ids.length > 0 && (
            <FormField>
              <FormLabel>{t('equipmentBundles.classesLabel')}</FormLabel>
              <ClassRows>
                {form.type_ids.map((typeId) => (
                  <ClassRow key={typeId}>
                    <span>{typeById[typeId] || `#${typeId}`}</span>
                    <SimpleSelect
                      data-cy={`equipment-bundles-class-${typeId}`}
                      value={form.class_by_type[typeId] || ''}
                      onChange={(val) => setForm((p) => ({ ...p, class_by_type: { ...p.class_by_type, [typeId]: val } }))}
                      options={[
                        { value: '', label: t('equipmentBundles.anyClass') },
                        ...classes.map((c) => ({ value: String(c.id), label: c.name })),
                      ]}
                    />
                  </ClassRow>
                ))}
              </ClassRows>
              <FormHint>{t('equipmentBundles.classesHint')}</FormHint>
            </FormField>
          )}

          <FormField>
            <FormLabel>{t('priceRules.appliesTo')}</FormLabel>
            <SimpleSelect
              data-cy="equipment-bundles-applies-to"
              value={form.applies_to}
              onChange={(val) => setForm((p) => ({ ...p, applies_to: val }))}
              options={[
                { value: 'all', label: t('priceRules.appliesToAll') },
                { value: 'equipment', label: t('priceRules.appliesToEquipment') },
                { value: 'classes', label: t('priceRules.appliesToClasses') },
              ]}
            />
            <FormHint>{t('equipmentBundles.appliesToHint')}</FormHint>
          </FormField>

          {formWarnings.length > 0 && (
            <Ostrzezenia role="status" data-cy="equipment-bundles-warnings">
              <strong>{t('bundleWarn.title')}</strong>
              <ul>
                {polaczOstrzezenia(formWarnings).map((w, i) => (
                  <li key={`${w.scope}-${w.code}-${w.period || i}`} data-code={w.code}>
                    {trescPolaczona(w, { t, typeById, classById })}
                  </li>
                ))}
              </ul>
              <small>{t('bundleWarn.footnote')}</small>
            </Ostrzezenia>
          )}

          <FormActions>
            <SecondaryButton type="button" onClick={closeForm}>{t('equipmentBundles.cancel')}</SecondaryButton>
            <PrimaryButton type="submit" data-cy="equipment-bundles-save" disabled={saving}>{t('equipmentBundles.save')}</PrimaryButton>
          </FormActions>
        </FormCard>
      )}

      {!loading && bundles.length === 0 && !formOpen && (
        <EmptyState data-tour="bundles-list">
          <p>{t('equipmentBundles.empty')}</p>
          <p>{t('equipmentBundles.emptyHint')}</p>
        </EmptyState>
      )}

      {bundles.length > 0 && (
        <TableWrap data-tour="bundles-list">
          <Table>
            <thead>
              <tr>
                <th>{t('equipmentBundles.name')}</th>
                <th>{t('equipmentBundles.columnTypes')}</th>
                <th>{t('equipmentBundles.columnPrice')}</th>
                <th>{t('equipmentBundles.columnActive')}</th>
                <th>{t('equipmentBundles.columnActions')}</th>
              </tr>
            </thead>
            {bundles.map((bundle) => {
                const isActive = bundle.active !== false;
                const ostrzezenia = Array.isArray(bundle.warnings) ? bundle.warnings : [];
                return (
                  <tbody key={bundle.id} className="pozycja">
                  <tr data-cy="equipment-bundles-row" style={{ opacity: isActive ? 1 : 0.5 }} className={ostrzezenia.length ? 'z-ostrzezeniem' : undefined}>
                    <td>
                      {bundle.name}
                      {rodzajZnacznika(ostrzezenia) && (
                        <Znacznik data-cy="equipment-bundles-badge">
                          {rodzajZnacznika(ostrzezenia) === 'never' && t('bundleWarn.badgeNever')}
                          {rodzajZnacznika(ostrzezenia) === 'partly' && t('bundleWarn.badgePartly')}
                          {rodzajZnacznika(ostrzezenia) === 'sometimes' && t('bundleWarn.badgeSometimes')}
                        </Znacznik>
                      )}
                    </td>
                    <td>{formatTypes(bundle)}</td>
                    <td>
                      {bundle.dailyPrice} zł/dzień
                      {bundle.dailyPriceOver7 != null && (
                        <CenaOd8>{t('equipmentBundles.over7Short', { price: bundle.dailyPriceOver7 })}</CenaOd8>
                      )}
                    </td>
                    <td>
                      <ActiveCell>
                        <ToggleSwitch
                          type="button"
                          role="switch"
                          aria-checked={isActive}
                          aria-label={t('equipmentBundles.toggleAria').replace('{name}', bundle.name)}
                          data-cy="equipment-bundles-active-toggle"
                          disabled={togglingId === bundle.id}
                          $on={isActive}
                          onClick={() => toggleBundleActive(bundle)}
                        />
                        <span>{isActive ? t('equipmentBundles.toggleOn') : t('equipmentBundles.toggleOff')}</span>
                      </ActiveCell>
                    </td>
                    <td>
                      <RowActions>
                        <SecondaryButton onClick={() => openEditForm(bundle)}>{t('equipmentBundles.edit')}</SecondaryButton>
                        <SecondaryButton onClick={() => handleDelete(bundle)}>{t('equipmentBundles.delete')}</SecondaryButton>
                      </RowActions>
                    </td>
                  </tr>
                  {/* Ostrzeżenia w osobnym wierszu na całą szerokość: w komórce nazwy (nowrap)
                      długie zdanie rozlewało się pod sąsiednie kolumny i było nieczytelne. */}
                  {ostrzezenia.length > 0 && (
                    <tr className="wiersz-ostrzezen" data-cy="equipment-bundles-row-warning" style={{ opacity: isActive ? 1 : 0.5 }}>
                      <td colSpan={5}>
                        <OstrzezenieWiersza>
                          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                          <ul>
                            {polaczOstrzezenia(ostrzezenia).map((w, i) => (
                              <li key={`${w.scope}-${w.code}-${w.period || i}`}>{trescPolaczona(w, { t, typeById, classById })}</li>
                            ))}
                          </ul>
                        </OstrzezenieWiersza>
                      </td>
                    </tr>
                  )}
                  </tbody>
                );
              })}
          </Table>
        </TableWrap>
      )}
      </TylkoOdczytWPrzerwie>
    </PanelContainer>
  );
}

const ErrorBanner = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  background: var(--danger-bg);
  border: 1px solid var(--danger-border);
  color: var(--danger);
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-muted);
  border: 1px dashed var(--input-border);
  border-radius: 16px;
`;

const FormCard = styled.form`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 1.1rem;
  color: var(--text);
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const FormField = styled.div`
  flex: 1;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
`;

const FormLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text);
`;

const FormHint = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-bottom: 8px;
  display: block;
`;

const FormInput = styled.input`
  padding: 9px 12px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.9rem;
`;

const TypeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

// Ostrzeżenie jak uwaga, nie jak błąd: zestaw da się zapisać, tylko nie zadziała w opisanej sytuacji.
const Ostrzezenia = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--warning) 40%, transparent);
  background: color-mix(in srgb, var(--warning) 8%, transparent);
  color: var(--text);
  font-size: 0.875rem;
  line-height: 1.45;

  strong { display: block; margin-bottom: 4px; color: var(--warning); }
  ul { margin: 0; padding-left: 18px; }
  li + li { margin-top: 4px; }
  small { display: block; margin-top: 6px; color: var(--text-muted); }
`;

// Wiersz ostrzeżeń pod zestawem: tekst zawija się normalnie, w kolorze tekstu (pomarańczowy
// drobnym drukiem był nieczytelny), z ikoną i lewą krawędzią w kolorze ostrzeżenia.
const OstrzezenieWiersza = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: var(--text-muted);
  font-size: 0.82rem;
  line-height: 1.45;
  white-space: normal;
  /* Na telefonie tabela przewija się w bok: ostrzeżenie zostaje w widocznej części. */
  position: sticky;
  left: 0;
  max-width: min(100%, calc(100vw - 64px));

  svg { flex: none; margin-top: 2px; color: var(--warning); }
  ul { margin: 0; padding: 0; list-style: none; }
  li + li { margin-top: 2px; }
`;

const Znacznik = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--warning);
  background: color-mix(in srgb, var(--warning) 12%, transparent);
`;

const CenaOd8 = styled.div`
  font-size: 0.78rem;
  color: var(--text-muted);
`;

const ClassRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 520px;
`;

const ClassRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  align-items: center;
  gap: 12px;

  > span { font-weight: 600; color: var(--text); overflow-wrap: anywhere; }
  @media (max-width: 480px) { grid-template-columns: minmax(0, 1fr); gap: 4px; }
`;

const TypeChip = styled.button`
  padding: 6px 12px;
  border-radius: 20px;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--primary)' : 'var(--card-bg)')};
  color: ${(p) => (p.$active ? 'white' : 'var(--text)')};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

const TableWrap = styled.div`
  overflow-x: auto;

  /* Przypieta pierwsza kolumna na ekranach dotykowych (V.6). Tabela jest szersza niz telefon,
     wiec po przewinieciu w bok bez tego nie wiadomo, ktorego wiersza dotycza widoczne kolumny.
     Zadna kolumna nie znika, przewijanie dziala jak dotad. Tlo musi byc nieprzezroczyste,
     inaczej przewijana tresc przeswituje pod spodem. Warunkiem jest DOTYK, nie sama szerokosc:
     tablet w poziomie ma 1024px i przy samym max-width zostalby bez przypiecia. */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    th:first-child,
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 3;
      background: var(--card-bg);
      box-shadow: 6px 0 8px -6px var(--shadow-color);
    }
  }

  border: 1px solid var(--input-border);
  border-radius: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px 14px;
    text-align: left;
    font-size: 0.85rem;
    border-bottom: 1px solid var(--input-border);
    color: var(--text);
    white-space: nowrap;
  }

  /* Wyśrodkowane SAME nagłówki (dane zostają z lewej) — kolumny mają bardzo różne
     szerokości (auto-layout: "Typy w zestawie" vs "Status"), lewo wyrównany nagłówek
     siedział przy lewej krawędzi wąskiej kolumny zamiast nad jej środkiem. */
  th {
    background: var(--table-header-bg);
    font-weight: 700;
    text-align: center;
  }

  /* Ostatnia kolumna (Akcje) kończyła się tuż przy prawej krawędzi tabeli. */
  th:last-child, td:last-child {
    padding-right: 24px;
  }

  /* Jeden zestaw = jeden <tbody class="pozycja">: wiersz zestawu i wiersz ostrzeżeń są jedną
     pozycją (wspólna kreska na dole, wspólne podświetlenie), a nie dwoma osobnymi polami. */
  tbody.pozycja td { border-bottom: none; }
  tbody.pozycja tr:last-child td { border-bottom: 1px solid var(--input-border); }
  tbody.pozycja:last-child tr:last-child td { border-bottom: none; }
  tbody.pozycja tr:hover { background: transparent; }
  tbody.pozycja:hover { background: var(--table-row-hover); }
  tr.wiersz-ostrzezen td {
    padding-top: 0;
    white-space: normal;
    position: static;
    box-shadow: none;
    background: transparent;
  }
`;

const RowActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const PresetsCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const PresetGrid = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const PresetCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  text-align: left;
  padding: 12px 16px;
  min-width: 200px;
  border: 1.5px dashed var(--input-border);
  border-radius: 12px;
  background: var(--input-bg);
  color: var(--text);
  cursor: pointer;

  &:hover { border-color: var(--primary); }

  strong { font-size: 0.9rem; }
  span { font-size: 0.8rem; color: var(--text-muted); }
  em { font-style: normal; font-size: 0.8rem; font-weight: 600; color: var(--success); }
`;

const ActiveCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  span { font-size: 0.8rem; color: var(--text-muted); }
`;
